/**
 * Glue between the Studio workspace UI and the Koko widget.
 *
 * Responsibilities:
 *  1. Builds the Anthropic tool definitions (`add_character`, etc.)
 *     that Claude can invoke to mutate StudioStore.
 *  2. Builds the per-turn system-prompt addition: workspace state
 *     summary + how-to-use-the-tools instructions + panel list.
 *  3. Routes incoming `tool_use` blocks to StudioStore mutators.
 *  4. Routes `[OPEN:panel_id]` directive tags to PanelManager.
 *
 * Exposed as a single `buildExtensions()` method so the studio
 * `main.ts` just hands the result straight to `kokoElement.setExtensions`.
 */

import type {
  AnthropicTool,
  KokoExtensions,
  ToolUseBlock,
} from "../types";
import type { KokoActionAgent } from "../agent/KokoActionAgent";
import {
  parseAndExtractDirectives,
  stripDirectivesFromStreamingText,
} from "./DirectiveTagParser";
import {
  PANEL_REGISTRY,
  type PanelManager,
  STUDIO_PANEL_IDS,
  type StudioPanelId,
} from "./PanelManager";
import type { StudioStore, AssetType } from "./StudioStore";
import {
  getKokoInteractionMode,
  isToolBlockedInSuggestionMode,
  resolveToolSubmit,
} from "./KokoInteractionMode";
// Koko's editable director playbook — the source of its directing
// behavior (voice, pipeline order, rules). `director.md` ships as the
// default, but the user can edit it LIVE from the Koko panel; the store
// returns the effective text (localStorage override if present, else the
// shipped file) so edits take effect without touching code or rebuilding.
import { getEffectiveDirectorPlaybook } from "./DirectorPlaybookStore";
import { LiveActionQueue } from "./LiveActionQueue";

const VALID_ASSET_TYPES: AssetType[] = [
  "prop",
  "vehicle",
  "set_piece",
  "wardrobe",
  "other",
];
const VALID_MOODBOARD_KINDS = ["image", "palette", "note", "lyric"] as const;
const VALID_SOUND_MODES = ["simple", "advanced"] as const;
const VALID_VOCAL_GENDERS = ["neutral", "feminine", "masculine"] as const;
const VALID_TIMELINE_TRACKS = ["bgm", "sfx", "dialogue", "storyboard", "video"] as const;

interface BridgeOptions {
  store: StudioStore;
  panelManager: PanelManager;
  /**
   * Action agent that drives the bird through visible UI actions
   * (fly + type + click). Optional — when absent (older deployments
   * or tests), tool_use calls fall back to direct store mutation
   * with no choreography.
   */
  actionAgent?: KokoActionAgent | null;
  /**
   * Fired once per turn AFTER every queued choreography for that
   * turn has finished. The host wires this to KokoApp.reportBack
   * which fires a follow-up Claude call so the bird "talks back"
   * about what just got done. The argument is the list of
   * action summaries collected during the turn (one per tool
   * call) — same strings that used to flash as pills.
   */
  onTurnComplete?: (actionsCompleted: string[]) => void;
}

export class StudioKokoBridge {
  private readonly store: StudioStore;
  private readonly panelManager: PanelManager;
  private readonly actionAgent: KokoActionAgent | null;
  private readonly onTurnComplete?: (actionsCompleted: string[]) => void;
  /**
   * Action summaries collected during the current turn (e.g.
   * "+ character: mei", "~ storyboard ready"). Reset whenever a
   * new turn's directive parser runs. Drained into onTurnComplete
   * after the choreography queue empties.
   */
  private actionsCollectedThisTurn: string[] = [];
  /**
   * Bumped every time the user interrupts (ESC / slash / new
   * tool_use turn). Each turn captures its own value at start of
   * choreography; the queue's tail callback only fires the
   * report-back if the snapshot still matches.
   */
  private interruptToken = 0;

  /**
   * Serial queue of in-flight choreographies. Each tool_use enqueues
   * a promise that opens the relevant panel, flies the bird through
   * the form, and clicks submit. Multiple tool_uses in one response
   * (e.g. "add three characters") play out one after the other.
   *
   * The queue is implemented as a chain on a single promise so we
   * never run two choreographies concurrently — the bird can only
   * be in one place at a time.
   */
  private choreographyQueue: Promise<void> = Promise.resolve();

  /**
   * Set true while at least one tool_use has fired this turn (the
   * bird is now busy with a choreography). Read by
   * `parseAndStripFinalDirectives` so it can cancel pointing /
   * suppress redundant `[OPEN:...]` switches — the choreography
   * already navigates panels itself.
   */
  private didEnqueueChoreographyThisTurn = false;

  /** Visible "what Koko is doing now" overlay with pause/stop. */
  private readonly queueUI: LiveActionQueue;
  /** Resolves immediately unless the queue is paused. */
  private pauseGate: Promise<void> = Promise.resolve();
  private resolvePauseGate: (() => void) | null = null;
  /** When true, remaining queued choreographies skip (Stop pressed). */
  private stopped = false;
  /** Friendly label for the tool currently being enqueued. */
  private pendingActionLabel = "";

  constructor(options: BridgeOptions) {
    this.store = options.store;
    this.panelManager = options.panelManager;
    this.actionAgent = options.actionAgent ?? null;
    this.onTurnComplete = options.onTurnComplete;
    this.queueUI = new LiveActionQueue({
      onPause: () => this.pauseQueue(),
      onResume: () => this.resumeQueue(),
      onStop: () => this.stopQueue(),
    });
    if (typeof document !== "undefined") {
      this.queueUI.mount(document.body);
    }
  }

  buildExtensions(): KokoExtensions {
    return {
      tools: this.buildToolDefinitions(),
      buildSystemPromptAddition: () => this.buildSystemPromptAddition(),
      onToolUse: (block) => this.handleToolUse(block),
      stripDirectivesFromStreamingText: (text) =>
        stripDirectivesFromStreamingText(text),
      parseAndStripFinalDirectives: (text) => {
        const { cleanedText, panelIdsToOpen } = parseAndExtractDirectives(text);
        const choreographyJustRan = this.didEnqueueChoreographyThisTurn;
        // Reset for next turn (this hook fires exactly once per turn).
        this.didEnqueueChoreographyThisTurn = false;

        // Honor [OPEN:...] only when no choreography is running —
        // the choreography handles its own panel navigation, so a
        // late synchronous open() would yank the panel out from
        // under the bird mid-flight.
        if (panelIdsToOpen.length > 0 && !choreographyJustRan) {
          const lastPanelId = panelIdsToOpen[panelIdsToOpen.length - 1];
          this.panelManager.open(lastPanelId);
        }

        // If this turn fired a choreography, schedule a report-back
        // to fire once the queue drains. The report is gated on
        // the interrupt token: if the user presses ESC or starts a
        // new turn, the token bumps and the closure short-circuits.
        if (choreographyJustRan) {
          const tokenAtSchedule = this.interruptToken;
          this.choreographyQueue = this.choreographyQueue.then(() => {
            if (tokenAtSchedule !== this.interruptToken) return;
            const actions = this.actionsCollectedThisTurn.slice();
            this.actionsCollectedThisTurn = [];
            if (actions.length === 0) return;
            this.onTurnComplete?.(actions);
          });
        }

        return {
          cleanedText,
          deferPointingUntilNextFrame:
            panelIdsToOpen.length > 0 && !choreographyJustRan,
          cancelPointing: choreographyJustRan,
        };
      },
      onUserInterrupt: () => {
        // Bump token so any chained report-back closures see a
        // changed value and bail. Also clear the queued actions
        // so the next turn doesn't carry over stale entries, and
        // stand the live queue down.
        this.interruptToken++;
        this.actionsCollectedThisTurn = [];
        this.stopped = true;
        this.resumeQueue();
        this.queueUI.markStopped();
      },
    };
  }

  // ------------------------------------------------------------------
  // System prompt
  // ------------------------------------------------------------------

  private buildSystemPromptAddition(): string {
    const panelMenu = STUDIO_PANEL_IDS
      .map((panelId) => `  ${panelId} — ${PANEL_REGISTRY[panelId].description}`)
      .join("\n");
    const currentPanelId = this.panelManager.getCurrentPanelId() ?? "location";
    const stateSummary = this.store.buildPromptSummary();
    const interactionMode = getKokoInteractionMode();
    const modeBlock =
      interactionMode === "suggestion"
        ? [
            "INTERACTION MODE: SUGGESTION (user toggle is on suggest)",
            "you draft and recommend — you do NOT commit destructive or state-changing work automatically.",
            "- for add/update tools (characters, assets, locations, mood board): ALWAYS pass submit=false. the bird types into forms but never clicks submit — the user reviews and saves.",
            "- do NOT call delete_*, add_scene, update_scene, set_costume, cast_scene, set_storyboard_status, update_sound_design, set_scene_camera, or set_project_title. describe what you'd do instead.",
            "- you MAY use [OPEN:panel_id] and jump_timeline_track to navigate the workspace.",
            "- keep spoken text brief; tell the user what you drafted and what they should approve next.",
          ].join("\n")
        : [
            "INTERACTION MODE: ACTION (user toggle is on act)",
            "you execute changes. call tools with submit=true (default) so the bird clicks buttons and commits.",
            "the bird visibly performs every tool call — types, clicks submit, toggles chips, deletes.",
          ].join("\n");

    return [
      "DIRECTOR PLAYBOOK (your editable brain — follow it):",
      getEffectiveDirectorPlaybook(),
      "",
      "STUDIO MODE",
      modeBlock,
      "",
      "you are running inside a video-production workspace. the user is making a short film. you have three capabilities beyond the normal koko behavior:",
      "",
      "1. switch panels with [OPEN:panel_id]:",
      `   valid panel_ids:`,
      panelMenu,
      `   current panel: ${currentPanelId}. only one [OPEN:...] per response.`,
      "",
      "   never combine [OPEN:...] with [POINT:...] in the same response — the screenshot you see is of the CURRENT panel, so if you switch panels those refs go stale. either point at something on the current panel (no open) or switch (no point).",
      "",
      "2. mutate workspace state with tool calls:",
      "   you can add / update / delete characters, assets, locations, scenes, and mood board pins; set casting, costume notes, sound design, camera framing, and storyboard status; jump timeline tracks; rename the project. call these tools whenever the user describes work — don't just narrate, do it. e.g. user says 'add ali, a tired filmmaker' → add_character(name='ali', description='a tired filmmaker').",
      "",
      "   THE BIRD VISIBLY PERFORMS YOUR TOOL CALLS. it flies to the relevant panel, types into inputs character-by-character, and clicks submit. consequences:",
      "   - DO NOT add [OPEN:...] when you call a tool — the bird opens panels itself.",
      "   - DO NOT add [POINT:...] when you call a tool — the bird is busy typing.",
      "   - DO NOT use any html or markup like <cite>...</cite> in tool inputs that get typed (descriptions, costume notes, scene bodies). plain prose only — those characters end up literally in form fields.",
      "   - keep your spoken text short. one sentence like 'adding mei now' is plenty. the typing IS the show.",
      "",
      "   each tool also accepts an optional `submit: false` parameter. when the user wants you to suggest / draft (e.g. 'fill it in but let me check before saving') pass `submit: false` — the bird types into the form but doesn't click the button. user reviews and submits manually.",
      "",
      "3. complete EVERY item the user asked for in this turn:",
      "   if the user says 'draft costumes for all five characters', you MUST call set_costume five times — once per character — in this turn. don't stop after two or three. don't ask 'should i continue?'. just do all the work the user requested. you have ample tokens (4096) and a serial action queue handles the choreography pacing.",
      "",
      "   chains are encouraged: 'fill in costumes then head to casting' = a stack of set_costume tool calls followed by [OPEN:casting] (the bird auto-navigates the costume panel for the costume calls; opening casting at the end is fine because by then the bird has finished typing — it's the trailing navigation, not a mid-action switch).",
      "",
      interactionMode === "action"
        ? "be a director's assistant: proactive, opinionated, brief. when the user asks for help, suggest concrete next steps (add this character, draft these costumes, mark storyboard ready) and then DO them, don't just describe them."
        : "be a director's assistant: proactive, opinionated, brief. in suggestion mode, draft what you can in forms and clearly state what still needs the user's click to commit.",
      "",
      "current workspace state:",
      stateSummary,
    ].join("\n");
  }

  // ------------------------------------------------------------------
  // Tool definitions
  // ------------------------------------------------------------------

  private buildToolDefinitions(): AnthropicTool[] {
    // `submit` is optional on every add/update tool. Default true =
    // bird types AND clicks the form's submit button. Pass false
    // when the user wants you to draft / suggest without committing
    // ('fill it in but let me check first').
    const submitProp = {
      submit: {
        type: "boolean",
        description:
          "Whether the bird should click the form's submit button after typing. Default: true. Pass false when the user wants you to draft/suggest only — they'll review and submit themselves.",
      },
    } as const;

    return [
      {
        name: "add_character",
        description:
          "Add a new character to the cast. Bird flies to the Characters panel, types into the form, optionally clicks Add.",
        input_schema: {
          type: "object",
          properties: {
            name: { type: "string", description: "Display name." },
            description: {
              type: "string",
              description:
                "Visual description, role, personality. Plain prose only — no html/markup. Used downstream for portrait + costume.",
            },
            ...submitProp,
          },
          required: ["name", "description"],
        },
      },
      {
        name: "update_character",
        description:
          "Edit an existing character. Pass the character_id and any fields you want to change (name and/or description). Bird flies to the card, opens edit mode, retypes the chosen fields, optionally clicks Save.",
        input_schema: {
          type: "object",
          properties: {
            character_id: { type: "string" },
            name: { type: "string", description: "New name (omit to keep current)." },
            description: {
              type: "string",
              description: "New description, plain prose (omit to keep current).",
            },
            ...submitProp,
          },
          required: ["character_id"],
        },
      },
      {
        name: "delete_character",
        description:
          "Remove a character from the cast by id. Bird flies to the card and clicks its Delete button.",
        input_schema: {
          type: "object",
          properties: { character_id: { type: "string" } },
          required: ["character_id"],
        },
      },
      {
        name: "add_asset",
        description:
          "Add a prop, vehicle, set piece, wardrobe item, or misc asset to the project.",
        input_schema: {
          type: "object",
          properties: {
            name: { type: "string" },
            type: {
              type: "string",
              enum: VALID_ASSET_TYPES,
              description: "Category of the asset.",
            },
            description: { type: "string", description: "Plain prose, no html." },
            ...submitProp,
          },
          required: ["name", "type", "description"],
        },
      },
      {
        name: "update_asset",
        description:
          "Edit an existing asset. Pass asset_id and any fields to change.",
        input_schema: {
          type: "object",
          properties: {
            asset_id: { type: "string" },
            name: { type: "string" },
            type: { type: "string", enum: VALID_ASSET_TYPES },
            description: { type: "string" },
            ...submitProp,
          },
          required: ["asset_id"],
        },
      },
      {
        name: "delete_asset",
        description: "Remove an asset by id. Bird flies to the card and clicks Delete.",
        input_schema: {
          type: "object",
          properties: { asset_id: { type: "string" } },
          required: ["asset_id"],
        },
      },
      {
        name: "add_location",
        description:
          "Add a shooting location. Richer descriptions (lighting, mood, layout) make the dummy preview better.",
        input_schema: {
          type: "object",
          properties: {
            name: { type: "string" },
            description: { type: "string", description: "Plain prose, no html." },
            ...submitProp,
          },
          required: ["name", "description"],
        },
      },
      {
        name: "update_location",
        description:
          "Edit an existing location. Pass location_id and any fields to change.",
        input_schema: {
          type: "object",
          properties: {
            location_id: { type: "string" },
            name: { type: "string" },
            description: { type: "string" },
            ...submitProp,
          },
          required: ["location_id"],
        },
      },
      {
        name: "delete_location",
        description: "Remove a location by id. Bird flies to the card and clicks Delete.",
        input_schema: {
          type: "object",
          properties: { location_id: { type: "string" } },
          required: ["location_id"],
        },
      },
      {
        name: "add_scene",
        description:
          "Append a new scene to the script. The bird clicks 'Add Scene' then types the body into the new scene's editor.",
        input_schema: {
          type: "object",
          properties: {
            body: {
              type: "string",
              description: "Full scene body. Plain prose, no html. Multiple lines OK.",
            },
            ...submitProp,
          },
          required: ["body"],
        },
      },
      {
        name: "update_scene",
        description:
          "Replace the body text of an existing scene. Bird flies to the scene editor and retypes it.",
        input_schema: {
          type: "object",
          properties: {
            scene_id: { type: "string" },
            body: { type: "string", description: "Plain prose, no html." },
          },
          required: ["scene_id", "body"],
        },
      },
      {
        name: "delete_scene",
        description: "Remove a scene from the script. Bird flies to the scene card and clicks Delete.",
        input_schema: {
          type: "object",
          properties: { scene_id: { type: "string" } },
          required: ["scene_id"],
        },
      },
      {
        name: "set_costume",
        description:
          "Set / replace the costume notes for a character. Bird flies to the costume textarea for that character and types the notes.",
        input_schema: {
          type: "object",
          properties: {
            character_id: { type: "string" },
            notes: {
              type: "string",
              description: "Costume description. Plain prose, no html.",
            },
          },
          required: ["character_id", "notes"],
        },
      },
      {
        name: "cast_scene",
        description:
          "Set the cast list for a scene. Replaces prior casting for that scene. Bird flies to the scene's chip row and clicks each character chip in order.",
        input_schema: {
          type: "object",
          properties: {
            scene_id: { type: "string" },
            character_ids: {
              type: "array",
              items: { type: "string" },
              description: "Character ids appearing in this scene.",
            },
          },
          required: ["scene_id", "character_ids"],
        },
      },
      {
        name: "set_storyboard_status",
        description:
          "Mark the storyboard as ready (or reset to empty). Bird flies to the storyboard panel and clicks the toggle button.",
        input_schema: {
          type: "object",
          properties: {
            status: { type: "string", enum: ["empty", "ready"] },
          },
          required: ["status"],
        },
      },
      {
        name: "add_moodboard_item",
        description:
          "Pin a new mood board / treatment item. Bird flies to the mood board panel, fills type/title/details, and optionally clicks Pin.",
        input_schema: {
          type: "object",
          properties: {
            kind: { type: "string", enum: [...VALID_MOODBOARD_KINDS] },
            title: { type: "string", description: "Short label for the board card." },
            detail: {
              type: "string",
              description: "The style signal in plain prose (no html/markup).",
            },
            ...submitProp,
          },
          required: ["kind", "title", "detail"],
        },
      },
      {
        name: "delete_moodboard_item",
        description:
          "Remove a mood board item by id. Bird flies to that card's Remove button and clicks it.",
        input_schema: {
          type: "object",
          properties: { item_id: { type: "string" } },
          required: ["item_id"],
        },
      },
      {
        name: "update_sound_design",
        description:
          "Patch sound settings (mode/style pills/vocal tone/weirdness/style influence/scene length). Bird opens Sound Studio and applies the controls.",
        input_schema: {
          type: "object",
          properties: {
            mode: { type: "string", enum: [...VALID_SOUND_MODES] },
            style_pills: {
              type: "array",
              items: { type: "string" },
              description:
                "Exact list of style pills to keep selected. Use known presets for visible chip toggles.",
            },
            vocal_gender: { type: "string", enum: [...VALID_VOCAL_GENDERS] },
            weirdness: { type: "number" },
            style_influence: { type: "number" },
            scene_length_sec: { type: "number" },
          },
        },
      },
      {
        name: "set_scene_camera",
        description:
          "Set per-scene camera framing (x/y/z/lens_mm). Bird flies to the scene's camera sliders and adjusts them.",
        input_schema: {
          type: "object",
          properties: {
            scene_id: { type: "string" },
            x: { type: "number" },
            y: { type: "number" },
            z: { type: "number" },
            lens_mm: { type: "number" },
          },
          required: ["scene_id", "x", "y", "z", "lens_mm"],
        },
      },
      {
        name: "jump_timeline_track",
        description:
          "Open timeline and jump via a track label or a specific scene beat. Useful for navigating into sound/dialogue/storyboard/video flows.",
        input_schema: {
          type: "object",
          properties: {
            track_id: { type: "string", enum: [...VALID_TIMELINE_TRACKS] },
            scene_id: {
              type: "string",
              description:
                "Optional: scene id whose beat should be clicked on that track. Omit to click the track label.",
            },
          },
          required: ["track_id"],
        },
      },
      {
        name: "set_project_title",
        description:
          "Rename the project. Bird flies to the title in the header and types the new name.",
        input_schema: {
          type: "object",
          properties: { title: { type: "string", description: "Plain prose." } },
          required: ["title"],
        },
      },
    ];
  }

  // ------------------------------------------------------------------
  // Tool dispatch
  // ------------------------------------------------------------------

  /**
   * Tool dispatch. Every state-changing tool runs through the same
   * choreography queue so the bird visibly performs each action one
   * at a time. Each choreographer falls back to direct store
   * mutation if its expected DOM controls aren't found (e.g. a
   * panel was never opened, missing data-koko-action attrs after a
   * future refactor) so the user's request still applies.
   *
   * Tools that take an optional `submit` parameter:
   *   - true (default) — bird types AND clicks the form's submit
   *     button (commits the change).
   *   - false — bird types but leaves the form open for the user
   *     to review and submit themselves. Used when the user asks
   *     Koko to "draft" or "suggest".
   */
  private handleToolUse(block: ToolUseBlock): void {
    const input = (block.input ?? {}) as Record<string, unknown>;
    const submit = resolveToolSubmit(readBoolean(input, "submit"));
    // Friendly label for the action-queue overlay — read by the next
    // enqueueChoreography call triggered by this tool.
    this.pendingActionLabel = describeTool(block);

    if (
      getKokoInteractionMode() === "suggestion" &&
      isToolBlockedInSuggestionMode(block.name)
    ) {
      this.fireApplied(`suggested ${block.name.replace(/_/g, " ")}`);
      return;
    }

    switch (block.name) {
      case "add_character": {
        const name = readString(input, "name");
        const description = readString(input, "description");
        if (!name || !description) return this.warnInvalid(block, "missing name or description");
        this.enqueueChoreography(() => this.choreographAddCharacter(name, description, submit));
        return;
      }
      case "update_character": {
        const id = readString(input, "character_id");
        if (!id) return this.warnInvalid(block, "missing character_id");
        const name = readString(input, "name");
        const description = readString(input, "description");
        if (name === null && description === null) {
          return this.warnInvalid(block, "no fields to update");
        }
        this.enqueueChoreography(() =>
          this.choreographUpdateCharacter(id, { name, description }, submit),
        );
        return;
      }
      case "delete_character": {
        const id = readString(input, "character_id");
        if (!id) return this.warnInvalid(block, "missing character_id");
        this.enqueueChoreography(() => this.choreographDelete("character", id));
        return;
      }
      case "add_asset": {
        const name = readString(input, "name");
        const description = readString(input, "description");
        const rawType = readString(input, "type") ?? "other";
        const type = VALID_ASSET_TYPES.includes(rawType as AssetType)
          ? (rawType as AssetType)
          : "other";
        if (!name || !description) return this.warnInvalid(block, "missing name or description");
        this.enqueueChoreography(() => this.choreographAddAsset(name, type, description, submit));
        return;
      }
      case "update_asset": {
        const id = readString(input, "asset_id");
        if (!id) return this.warnInvalid(block, "missing asset_id");
        const name = readString(input, "name");
        const description = readString(input, "description");
        const rawType = readString(input, "type");
        const type = rawType
          ? (VALID_ASSET_TYPES.includes(rawType as AssetType)
              ? (rawType as AssetType)
              : null)
          : null;
        if (name === null && description === null && type === null) {
          return this.warnInvalid(block, "no fields to update");
        }
        this.enqueueChoreography(() =>
          this.choreographUpdateAsset(id, { name, type, description }, submit),
        );
        return;
      }
      case "delete_asset": {
        const id = readString(input, "asset_id");
        if (!id) return this.warnInvalid(block, "missing asset_id");
        this.enqueueChoreography(() => this.choreographDelete("asset", id));
        return;
      }
      case "add_location": {
        const name = readString(input, "name");
        const description = readString(input, "description");
        if (!name || !description) return this.warnInvalid(block, "missing name or description");
        this.enqueueChoreography(() => this.choreographAddLocation(name, description, submit));
        return;
      }
      case "update_location": {
        const id = readString(input, "location_id");
        if (!id) return this.warnInvalid(block, "missing location_id");
        const name = readString(input, "name");
        const description = readString(input, "description");
        if (name === null && description === null) {
          return this.warnInvalid(block, "no fields to update");
        }
        this.enqueueChoreography(() =>
          this.choreographUpdateLocation(id, { name, description }, submit),
        );
        return;
      }
      case "delete_location": {
        const id = readString(input, "location_id");
        if (!id) return this.warnInvalid(block, "missing location_id");
        this.enqueueChoreography(() => this.choreographDelete("location", id));
        return;
      }
      case "add_scene": {
        const body = readString(input, "body");
        if (!body) return this.warnInvalid(block, "missing body");
        this.enqueueChoreography(() => this.choreographAddScene(body));
        return;
      }
      case "update_scene": {
        const id = readString(input, "scene_id");
        const body = readString(input, "body");
        if (!id || body === null) return this.warnInvalid(block, "missing scene_id or body");
        this.enqueueChoreography(() => this.choreographUpdateScene(id, body));
        return;
      }
      case "delete_scene": {
        const id = readString(input, "scene_id");
        if (!id) return this.warnInvalid(block, "missing scene_id");
        this.enqueueChoreography(() => this.choreographDelete("scene", id));
        return;
      }
      case "set_costume": {
        const characterId = readString(input, "character_id");
        const notes = readString(input, "notes");
        if (!characterId || notes === null) return this.warnInvalid(block, "missing character_id or notes");
        this.enqueueChoreography(() => this.choreographSetCostume(characterId, notes));
        return;
      }
      case "cast_scene": {
        const sceneId = readString(input, "scene_id");
        const characterIds = readStringArray(input, "character_ids");
        if (!sceneId || !characterIds) return this.warnInvalid(block, "missing scene_id or character_ids");
        this.enqueueChoreography(() => this.choreographCastScene(sceneId, characterIds));
        return;
      }
      case "set_storyboard_status": {
        const status = readString(input, "status");
        if (status !== "empty" && status !== "ready") {
          return this.warnInvalid(block, `invalid status: ${status}`);
        }
        this.enqueueChoreography(() => this.choreographSetStoryboardStatus(status));
        return;
      }
      case "add_moodboard_item": {
        const kind = readString(input, "kind");
        const title = readString(input, "title");
        const detail = readString(input, "detail");
        if (!kind || !title || !detail) {
          return this.warnInvalid(block, "missing kind, title, or detail");
        }
        if (!VALID_MOODBOARD_KINDS.includes(kind as (typeof VALID_MOODBOARD_KINDS)[number])) {
          return this.warnInvalid(block, `invalid kind: ${kind}`);
        }
        this.enqueueChoreography(() =>
          this.choreographAddMoodBoardItem(
            kind as (typeof VALID_MOODBOARD_KINDS)[number],
            title,
            detail,
            submit,
          ),
        );
        return;
      }
      case "delete_moodboard_item": {
        const id = readString(input, "item_id");
        if (!id) return this.warnInvalid(block, "missing item_id");
        this.enqueueChoreography(() => this.choreographDeleteMoodBoardItem(id));
        return;
      }
      case "update_sound_design": {
        const mode = readString(input, "mode");
        const vocalGender = readString(input, "vocal_gender");
        const stylePills = readStringArray(input, "style_pills");
        const weirdness = readNumber(input, "weirdness");
        const styleInfluence = readNumber(input, "style_influence");
        const sceneLengthSec = readNumber(input, "scene_length_sec");
        const patch: Partial<{
          mode: "simple" | "advanced";
          stylePills: string[];
          vocalGender: "neutral" | "feminine" | "masculine";
          weirdness: number;
          styleInfluence: number;
          sceneLengthSec: number;
        }> = {};
        if (mode !== null) {
          if (!VALID_SOUND_MODES.includes(mode as (typeof VALID_SOUND_MODES)[number])) {
            return this.warnInvalid(block, `invalid mode: ${mode}`);
          }
          patch.mode = mode as "simple" | "advanced";
        }
        if (stylePills !== null) patch.stylePills = stylePills;
        if (vocalGender !== null) {
          if (
            !VALID_VOCAL_GENDERS.includes(
              vocalGender as (typeof VALID_VOCAL_GENDERS)[number],
            )
          ) {
            return this.warnInvalid(block, `invalid vocal_gender: ${vocalGender}`);
          }
          patch.vocalGender = vocalGender as "neutral" | "feminine" | "masculine";
        }
        if (weirdness !== null) patch.weirdness = clamp(weirdness, 0, 100);
        if (styleInfluence !== null) patch.styleInfluence = clamp(styleInfluence, 0, 100);
        if (sceneLengthSec !== null) patch.sceneLengthSec = Math.max(4, sceneLengthSec);
        if (Object.keys(patch).length === 0) {
          return this.warnInvalid(block, "no sound fields to update");
        }
        this.enqueueChoreography(() => this.choreographUpdateSoundDesign(patch));
        return;
      }
      case "set_scene_camera": {
        const sceneId = readString(input, "scene_id");
        const x = readNumber(input, "x");
        const y = readNumber(input, "y");
        const z = readNumber(input, "z");
        const lensMm = readNumber(input, "lens_mm");
        if (!sceneId || x === null || y === null || z === null || lensMm === null) {
          return this.warnInvalid(block, "missing scene_id, x, y, z, or lens_mm");
        }
        this.enqueueChoreography(() =>
          this.choreographSetSceneCamera(sceneId, {
            x: clamp(x, -100, 100),
            y: clamp(y, -100, 100),
            z: clamp(z, -100, 100),
            lensMm: clamp(lensMm, 16, 120),
          }),
        );
        return;
      }
      case "jump_timeline_track": {
        const trackId = readString(input, "track_id");
        const sceneId = readString(input, "scene_id");
        if (
          !trackId ||
          !VALID_TIMELINE_TRACKS.includes(trackId as (typeof VALID_TIMELINE_TRACKS)[number])
        ) {
          return this.warnInvalid(block, `invalid track_id: ${trackId}`);
        }
        this.enqueueChoreography(() =>
          this.choreographJumpTimelineTrack(
            trackId as (typeof VALID_TIMELINE_TRACKS)[number],
            sceneId,
          ),
        );
        return;
      }
      case "set_project_title": {
        const title = readString(input, "title");
        if (!title) return this.warnInvalid(block, "missing title");
        this.enqueueChoreography(() => this.choreographSetProjectTitle(title));
        return;
      }
      default: {
        console.warn(`[studio] unhandled tool name: ${block.name}`, block);
      }
    }
  }

  // ------------------------------------------------------------------
  // Choreography queue
  // ------------------------------------------------------------------

  /**
   * Append an async choreography to the serial queue. The queue
   * runs jobs one at a time (the bird can only be in one place),
   * but enqueueing is non-blocking — `handleToolUse` returns
   * synchronously to keep the SSE stream parser flowing.
   */
  private enqueueChoreography(asyncJob: () => Promise<void>): void {
    // First enqueue of a new turn clears any prior Stop so the bird works again.
    if (!this.didEnqueueChoreographyThisTurn) this.stopped = false;
    this.didEnqueueChoreographyThisTurn = true;

    // Register this action in the live queue overlay (queued → doing → done).
    const itemId = this.queueUI.add(this.pendingActionLabel || "working…");

    // Gate each job on the pause/stop controls before it runs.
    const runGated = async (): Promise<void> => {
      if (this.stopped) {
        this.queueUI.setStatus(itemId, "skipped");
        return;
      }
      await this.pauseGate;
      if (this.stopped) {
        this.queueUI.setStatus(itemId, "skipped");
        return;
      }
      this.queueUI.setStatus(itemId, "doing");
      try {
        await asyncJob();
      } finally {
        this.queueUI.setStatus(itemId, "done");
      }
    };

    if (!this.actionAgent) {
      // No agent — fall through to direct mutation in the job's
      // catch block (each choreographer is built defensively).
      this.choreographyQueue = this.choreographyQueue.then(() =>
        runGated().catch((error) => {
          console.warn("[studio] choreography fallback:", error);
        }),
      );
      return;
    }
    const agent = this.actionAgent;
    this.choreographyQueue = this.choreographyQueue.then(async () => {
      agent.beginChoreography();
      try {
        await runGated();
      } catch (error) {
        console.warn("[studio] choreography error:", error);
      } finally {
        agent.endChoreography();
      }
    });
  }

  /** Pause the choreography queue — the in-flight job finishes, the rest wait. */
  private pauseQueue(): void {
    if (this.resolvePauseGate) return; // already paused
    this.pauseGate = new Promise<void>((resolve) => {
      this.resolvePauseGate = resolve;
    });
  }

  private resumeQueue(): void {
    this.resolvePauseGate?.();
    this.resolvePauseGate = null;
    this.pauseGate = Promise.resolve();
  }

  /** Stop: remaining queued actions skip; the bird stands down. */
  private stopQueue(): void {
    this.stopped = true;
    this.resumeQueue();
    this.interruptToken++;
    this.actionsCollectedThisTurn = [];
  }

  // ------------------------------------------------------------------
  // Choreographers — each opens the relevant panel, types into the
  // form, clicks submit. Falls back to direct store mutation on any
  // failure so the user's request still applies.
  // ------------------------------------------------------------------

  private async choreographAddCharacter(
    name: string,
    description: string,
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) {
      if (submit) this.store.addCharacter(name, description);
      return;
    }
    try {
      await this.openPanelAndSettle("characters");
      const nameInput = this.queryAction<HTMLInputElement>("character-name");
      const descInput = this.queryAction<HTMLTextAreaElement>("character-description");
      const submitButton = this.queryAction<HTMLButtonElement>("character-submit");
      if (!nameInput || !descInput || !submitButton) {
        throw new Error("character creator inputs not found");
      }
      await agent.flyTo(nameInput);
      await agent.typeInto(nameInput, name);
      await agent.flyTo(descInput);
      await agent.typeInto(descInput, description);
      if (submit) {
        await agent.flyTo(submitButton);
        await agent.click(submitButton);
        this.fireApplied(`+ character: ${name}`);
      } else {
        this.fireApplied(`drafted ${name} — review + submit`);
      }
    } catch (error) {
      console.warn("[studio] add_character choreography failed, mutating directly:", error);
      if (submit) this.store.addCharacter(name, description);
    } finally {
      agent.perch();
    }
  }

  private async choreographUpdateCharacter(
    characterId: string,
    patch: { name: string | null; description: string | null },
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) {
      if (submit) this.applyCharacterUpdateDirectly(characterId, patch);
      return;
    }
    try {
      await this.openPanelAndSettle("characters");
      const editButton = this.queryActionForId<HTMLButtonElement>(
        "character-edit",
        characterId,
      );
      if (!editButton) {
        throw new Error(`character ${characterId} edit button not found`);
      }
      await agent.flyTo(editButton);
      await agent.click(editButton);
      await agent.waitForPaint();

      if (patch.name !== null) {
        const nameInput = this.queryActionForId<HTMLInputElement>(
          "character-edit-name",
          characterId,
        );
        if (nameInput) {
          await agent.flyTo(nameInput);
          await agent.typeInto(nameInput, patch.name);
        }
      }
      if (patch.description !== null) {
        const descInput = this.queryActionForId<HTMLTextAreaElement>(
          "character-edit-description",
          characterId,
        );
        if (descInput) {
          await agent.flyTo(descInput);
          await agent.typeInto(descInput, patch.description);
        }
      }

      if (submit) {
        const saveButton = this.queryActionForId<HTMLButtonElement>(
          "character-edit-save",
          characterId,
        );
        if (saveButton) {
          await agent.flyTo(saveButton);
          await agent.click(saveButton);
        }
        this.fireApplied(`~ character ${patch.name ?? characterId}`);
      } else {
        this.fireApplied(`drafted edit on ${characterId}`);
      }
    } catch (error) {
      console.warn("[studio] update_character choreography failed, mutating directly:", error);
      if (submit) this.applyCharacterUpdateDirectly(characterId, patch);
    } finally {
      agent.perch();
    }
  }

  private applyCharacterUpdateDirectly(
    characterId: string,
    patch: { name: string | null; description: string | null },
  ): void {
    const directPatch: Partial<{ name: string; description: string }> = {};
    if (patch.name !== null) directPatch.name = patch.name;
    if (patch.description !== null) directPatch.description = patch.description;
    this.store.updateCharacter(characterId, directPatch);
  }

  private async choreographAddAsset(
    name: string,
    type: AssetType,
    description: string,
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) {
      if (submit) this.store.addAsset(name, type, description);
      return;
    }
    try {
      await this.openPanelAndSettle("assets");
      const nameInput = this.queryAction<HTMLInputElement>("asset-name");
      const typeSelect = this.queryAction<HTMLSelectElement>("asset-type");
      const descInput = this.queryAction<HTMLTextAreaElement>("asset-description");
      const submitButton = this.queryAction<HTMLButtonElement>("asset-submit");
      if (!nameInput || !typeSelect || !descInput || !submitButton) {
        throw new Error("asset form inputs not found");
      }
      await agent.flyTo(nameInput);
      await agent.typeInto(nameInput, name);
      // Dropdown — fly to it (visual signal) then set value directly.
      await agent.flyTo(typeSelect);
      typeSelect.value = type;
      typeSelect.dispatchEvent(new Event("change", { bubbles: true }));
      await agent.flyTo(descInput);
      await agent.typeInto(descInput, description);
      if (submit) {
        await agent.flyTo(submitButton);
        await agent.click(submitButton);
        this.fireApplied(`+ asset: ${name}`);
      } else {
        this.fireApplied(`drafted asset ${name}`);
      }
    } catch (error) {
      console.warn("[studio] add_asset choreography failed, mutating directly:", error);
      if (submit) this.store.addAsset(name, type, description);
    } finally {
      agent.perch();
    }
  }

  private async choreographUpdateAsset(
    assetId: string,
    patch: {
      name: string | null;
      type: AssetType | null;
      description: string | null;
    },
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) {
      if (submit) this.applyAssetUpdateDirectly(assetId, patch);
      return;
    }
    try {
      await this.openPanelAndSettle("assets");
      const editButton = this.queryActionForId<HTMLButtonElement>(
        "asset-edit",
        assetId,
      );
      if (!editButton) throw new Error(`asset ${assetId} edit button not found`);
      await agent.flyTo(editButton);
      await agent.click(editButton);
      await agent.waitForPaint();

      if (patch.name !== null) {
        const nameInput = this.queryActionForId<HTMLInputElement>(
          "asset-edit-name",
          assetId,
        );
        if (nameInput) {
          await agent.flyTo(nameInput);
          await agent.typeInto(nameInput, patch.name);
        }
      }
      if (patch.type !== null) {
        const typeSelect = this.queryActionForId<HTMLSelectElement>(
          "asset-edit-type",
          assetId,
        );
        if (typeSelect) {
          await agent.flyTo(typeSelect);
          typeSelect.value = patch.type;
          typeSelect.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
      if (patch.description !== null) {
        const descInput = this.queryActionForId<HTMLTextAreaElement>(
          "asset-edit-description",
          assetId,
        );
        if (descInput) {
          await agent.flyTo(descInput);
          await agent.typeInto(descInput, patch.description);
        }
      }
      if (submit) {
        const saveButton = this.queryActionForId<HTMLButtonElement>(
          "asset-edit-save",
          assetId,
        );
        if (saveButton) {
          await agent.flyTo(saveButton);
          await agent.click(saveButton);
        }
        this.fireApplied(`~ asset ${patch.name ?? assetId}`);
      } else {
        this.fireApplied(`drafted edit on ${assetId}`);
      }
    } catch (error) {
      console.warn("[studio] update_asset choreography failed, mutating directly:", error);
      if (submit) this.applyAssetUpdateDirectly(assetId, patch);
    } finally {
      agent.perch();
    }
  }

  private applyAssetUpdateDirectly(
    assetId: string,
    patch: {
      name: string | null;
      type: AssetType | null;
      description: string | null;
    },
  ): void {
    const directPatch: Partial<{
      name: string;
      type: AssetType;
      description: string;
    }> = {};
    if (patch.name !== null) directPatch.name = patch.name;
    if (patch.type !== null) directPatch.type = patch.type;
    if (patch.description !== null) directPatch.description = patch.description;
    this.store.updateAsset(assetId, directPatch);
  }

  private async choreographAddLocation(
    name: string,
    description: string,
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) {
      if (submit) this.store.addLocation(name, description);
      return;
    }
    try {
      await this.openPanelAndSettle("location");
      const nameInput = this.queryAction<HTMLInputElement>("location-name");
      const descInput = this.queryAction<HTMLTextAreaElement>("location-description");
      const submitButton = this.queryAction<HTMLButtonElement>("location-submit");
      if (!nameInput || !descInput || !submitButton) {
        throw new Error("location form inputs not found");
      }
      await agent.flyTo(nameInput);
      await agent.typeInto(nameInput, name);
      await agent.flyTo(descInput);
      await agent.typeInto(descInput, description);
      if (submit) {
        await agent.flyTo(submitButton);
        await agent.click(submitButton);
        this.fireApplied(`+ location: ${name}`);
      } else {
        this.fireApplied(`drafted location ${name}`);
      }
    } catch (error) {
      console.warn("[studio] add_location choreography failed, mutating directly:", error);
      if (submit) this.store.addLocation(name, description);
    } finally {
      agent.perch();
    }
  }

  private async choreographUpdateLocation(
    locationId: string,
    patch: { name: string | null; description: string | null },
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) {
      if (submit) this.applyLocationUpdateDirectly(locationId, patch);
      return;
    }
    try {
      await this.openPanelAndSettle("location");
      const editButton = this.queryActionForId<HTMLButtonElement>(
        "location-edit",
        locationId,
      );
      if (!editButton) throw new Error(`location ${locationId} edit button not found`);
      await agent.flyTo(editButton);
      await agent.click(editButton);
      await agent.waitForPaint();

      if (patch.name !== null) {
        const nameInput = this.queryActionForId<HTMLInputElement>(
          "location-edit-name",
          locationId,
        );
        if (nameInput) {
          await agent.flyTo(nameInput);
          await agent.typeInto(nameInput, patch.name);
        }
      }
      if (patch.description !== null) {
        const descInput = this.queryActionForId<HTMLTextAreaElement>(
          "location-edit-description",
          locationId,
        );
        if (descInput) {
          await agent.flyTo(descInput);
          await agent.typeInto(descInput, patch.description);
        }
      }
      if (submit) {
        const saveButton = this.queryActionForId<HTMLButtonElement>(
          "location-edit-save",
          locationId,
        );
        if (saveButton) {
          await agent.flyTo(saveButton);
          await agent.click(saveButton);
        }
        this.fireApplied(`~ location ${patch.name ?? locationId}`);
      } else {
        this.fireApplied(`drafted edit on ${locationId}`);
      }
    } catch (error) {
      console.warn("[studio] update_location choreography failed, mutating directly:", error);
      if (submit) this.applyLocationUpdateDirectly(locationId, patch);
    } finally {
      agent.perch();
    }
  }

  private applyLocationUpdateDirectly(
    locationId: string,
    patch: { name: string | null; description: string | null },
  ): void {
    const directPatch: Partial<{ name: string; description: string }> = {};
    if (patch.name !== null) directPatch.name = patch.name;
    if (patch.description !== null) directPatch.description = patch.description;
    this.store.updateLocation(locationId, directPatch);
  }

  private async choreographAddScene(body: string): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) {
      this.store.addScene(body);
      return;
    }
    try {
      await this.openPanelAndSettle("script_editor");
      const addSceneButton = this.queryAction<HTMLButtonElement>("scene-add");
      if (!addSceneButton) {
        throw new Error("add-scene button not found");
      }
      await agent.flyTo(addSceneButton);
      await agent.click(addSceneButton);
      await agent.waitForPaint();

      const allSceneBodies = document.querySelectorAll<HTMLElement>(
        '[data-koko-action="scene-body"]',
      );
      const newSceneBody = allSceneBodies[allSceneBodies.length - 1];
      if (!newSceneBody) {
        throw new Error("new scene body not found after click");
      }
      await agent.flyTo(newSceneBody);
      await agent.typeInto(newSceneBody, body);
      this.fireApplied(`+ scene`);
    } catch (error) {
      console.warn("[studio] add_scene choreography failed, mutating directly:", error);
      this.store.addScene(body);
    } finally {
      agent.perch();
    }
  }

  private async choreographUpdateScene(
    sceneId: string,
    body: string,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) {
      this.store.updateScene(sceneId, body);
      return;
    }
    try {
      await this.openPanelAndSettle("script_editor");
      const sceneBody = document.querySelector<HTMLElement>(
        `[data-koko-action="scene-body"][data-scene-id="${cssEscape(sceneId)}"]`,
      );
      if (!sceneBody) {
        throw new Error(`scene body for ${sceneId} not found`);
      }
      await agent.flyTo(sceneBody);
      await agent.typeInto(sceneBody, body);
      this.fireApplied(`~ scene ${sceneId}`);
    } catch (error) {
      console.warn("[studio] update_scene choreography failed, mutating directly:", error);
      this.store.updateScene(sceneId, body);
    } finally {
      agent.perch();
    }
  }

  private async choreographSetCostume(
    characterId: string,
    notes: string,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) {
      this.store.setCostume(characterId, notes);
      return;
    }
    try {
      await this.openPanelAndSettle("costume");
      const textarea = document.querySelector<HTMLTextAreaElement>(
        `[data-koko-action="costume"][data-character-id="${cssEscape(characterId)}"]`,
      );
      if (!textarea) {
        throw new Error(`costume textarea for character ${characterId} not found`);
      }
      await agent.flyTo(textarea);
      await agent.typeInto(textarea, notes);
      this.fireApplied(`~ costume for ${characterId}`);
    } catch (error) {
      console.warn("[studio] set_costume choreography failed, mutating directly:", error);
      this.store.setCostume(characterId, notes);
    } finally {
      agent.perch();
    }
  }

  /**
   * Universal "fly to a card's delete button and click it" handler.
   * Works for any kind of card whose delete button is tagged with
   * `data-koko-action="${kind}-delete" data-id="${id}"`.
   */
  private async choreographDelete(
    kind: "character" | "asset" | "location" | "scene",
    id: string,
  ): Promise<void> {
    const agent = this.actionAgent;
    const panelByKind: Record<typeof kind, StudioPanelId> = {
      character: "characters",
      asset: "assets",
      location: "location",
      scene: "script_editor",
    };
    const fallbackByKind: Record<typeof kind, () => void> = {
      character: () => this.store.deleteCharacter(id),
      asset: () => this.store.deleteAsset(id),
      location: () => this.store.deleteLocation(id),
      scene: () => this.store.deleteScene(id),
    };
    if (!agent) {
      fallbackByKind[kind]();
      return;
    }
    try {
      await this.openPanelAndSettle(panelByKind[kind]);
      const deleteButton = this.queryActionForId<HTMLButtonElement>(
        `${kind}-delete`,
        id,
      );
      if (!deleteButton) {
        throw new Error(`${kind} ${id} delete button not found`);
      }
      await agent.flyTo(deleteButton);
      await agent.click(deleteButton);
      this.fireApplied(`– ${kind} ${id}`);
    } catch (error) {
      console.warn(`[studio] delete_${kind} choreography failed, mutating directly:`, error);
      fallbackByKind[kind]();
    } finally {
      agent.perch();
    }
  }

  /**
   * Toggle the casting chips for a scene to match the requested
   * `character_ids` list. Bird flies to the chip row, then clicks
   * each chip whose state needs to flip (selected ↔ unselected).
   */
  // eslint-disable-next-line @typescript-eslint/require-await
  private async choreographCastScene(
    sceneId: string,
    characterIds: string[],
  ): Promise<void> {
    // Casting panel dropped (G2): cast is auto-derived from script
    // dialogue (see StudioStore.getCastingForScene). There are no chips
    // to drive, so apply the requested ids as a manual override directly.
    this.store.setCasting(sceneId, characterIds);
    this.fireApplied(`~ casting for ${sceneId}`);
  }

  private async choreographSetStoryboardStatus(
    status: "empty" | "ready",
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) {
      this.store.setStoryboardStatus(status);
      return;
    }
    try {
      await this.openPanelAndSettle("storyboard");
      // The toggle button is only rendered when prerequisites are
      // met (script + characters + locations exist). If it's missing,
      // mutate directly so the requested state still lands.
      const toggleButton = this.queryAction<HTMLButtonElement>(
        "storyboard-toggle",
      );
      const currentStatus = this.store.get("storyboardStatus");
      if (!toggleButton) {
        this.store.setStoryboardStatus(status);
      } else if (currentStatus !== status) {
        await agent.flyTo(toggleButton);
        await agent.click(toggleButton);
      }
      this.fireApplied(`storyboard ${status}`);
    } catch (error) {
      console.warn("[studio] set_storyboard_status choreography failed, mutating directly:", error);
      this.store.setStoryboardStatus(status);
    } finally {
      agent.perch();
    }
  }

  private async choreographAddMoodBoardItem(
    kind: (typeof VALID_MOODBOARD_KINDS)[number],
    title: string,
    detail: string,
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) {
      if (submit) this.store.addMoodBoardItem(kind, title, detail);
      return;
    }
    try {
      await this.openPanelAndSettle("moodboard");
      const kindInput = this.queryAction<HTMLSelectElement>("moodboard-kind");
      const titleInput = this.queryAction<HTMLInputElement>("moodboard-title");
      const detailInput = this.queryAction<HTMLTextAreaElement>("moodboard-detail");
      const submitButton = this.queryAction<HTMLButtonElement>("moodboard-submit");
      if (!kindInput || !titleInput || !detailInput || !submitButton) {
        throw new Error("mood board form inputs not found");
      }
      await agent.flyTo(kindInput);
      kindInput.value = kind;
      kindInput.dispatchEvent(new Event("change", { bubbles: true }));
      await agent.flyTo(titleInput);
      await agent.typeInto(titleInput, title);
      await agent.flyTo(detailInput);
      await agent.typeInto(detailInput, detail);
      if (submit) {
        await agent.flyTo(submitButton);
        await agent.click(submitButton);
        this.fireApplied(`+ mood board item: ${title}`);
      } else {
        this.fireApplied(`drafted mood board item ${title}`);
      }
    } catch (error) {
      console.warn("[studio] add_moodboard_item choreography failed, mutating directly:", error);
      if (submit) this.store.addMoodBoardItem(kind, title, detail);
    } finally {
      agent.perch();
    }
  }

  private async choreographDeleteMoodBoardItem(itemId: string): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) {
      this.store.deleteMoodBoardItem(itemId);
      return;
    }
    try {
      await this.openPanelAndSettle("moodboard");
      const deleteButton = this.queryActionForId<HTMLButtonElement>(
        "moodboard-remove",
        itemId,
      );
      if (!deleteButton) {
        throw new Error(`moodboard item ${itemId} remove button not found`);
      }
      await agent.flyTo(deleteButton);
      await agent.click(deleteButton);
      this.fireApplied(`– mood board ${itemId}`);
    } catch (error) {
      console.warn("[studio] delete_moodboard_item choreography failed, mutating directly:", error);
      this.store.deleteMoodBoardItem(itemId);
    } finally {
      agent.perch();
    }
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  private async choreographUpdateSoundDesign(
    patch: Partial<{
      mode: "simple" | "advanced";
      stylePills: string[];
      vocalGender: "neutral" | "feminine" | "masculine";
      weirdness: number;
      styleInfluence: number;
      sceneLengthSec: number;
    }>,
  ): Promise<void> {
    // Sound panel dropped (G8): per-dialogue sound now lives in the
    // Script Editor and the global background score in the Timeline.
    // There is no standalone panel to choreograph, so apply directly.
    this.store.updateSoundDesign(patch);
    this.fireApplied("~ sound design");
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  private async choreographSetSceneCamera(
    sceneId: string,
    camera: { x: number; y: number; z: number; lensMm: number },
  ): Promise<void> {
    // Camera Lab panel dropped (G8): the camera-angle adjuster is now a
    // per-frame tool owned by the Storyboard panel. Apply directly.
    this.store.setSceneCamera(sceneId, camera);
    this.fireApplied(`~ camera for ${sceneId}`);
  }

  private async choreographJumpTimelineTrack(
    trackId: (typeof VALID_TIMELINE_TRACKS)[number],
    sceneId: string | null,
  ): Promise<void> {
    const agent = this.actionAgent;
    const clickTimelineTarget = async (): Promise<boolean> => {
      if (sceneId) {
        const beat = document.querySelector<HTMLButtonElement>(
          `[data-koko-action="timeline-track-beat"][data-track-id="${cssEscape(trackId)}"][data-scene-id="${cssEscape(sceneId)}"]`,
        );
        if (beat) {
          if (agent) {
            await agent.flyTo(beat);
            await agent.click(beat);
          } else {
            beat.click();
          }
          return true;
        }
      }
      const label = document.querySelector<HTMLButtonElement>(
        `[data-koko-action="timeline-track-jump"][data-track-id="${cssEscape(trackId)}"]`,
      );
      if (label) {
        if (agent) {
          await agent.flyTo(label);
          await agent.click(label);
        } else {
          label.click();
        }
        return true;
      }
      return false;
    };
    if (!agent) {
      this.panelManager.open("timeline");
      if (!(await clickTimelineTarget())) {
        this.openTimelineTrackFallback(trackId);
      }
      return;
    }
    try {
      await this.openPanelAndSettle("timeline");
      const clicked = await clickTimelineTarget();
      if (!clicked) {
        this.openTimelineTrackFallback(trackId);
      }
      this.fireApplied(`opened ${trackId} timeline track`);
    } catch (error) {
      console.warn("[studio] jump_timeline_track choreography failed, opening directly:", error);
      this.openTimelineTrackFallback(trackId);
    } finally {
      agent.perch();
    }
  }

  private openTimelineTrackFallback(
    trackId: (typeof VALID_TIMELINE_TRACKS)[number],
  ): void {
    // Sound panel dropped (G8): the global background score lives in the
    // Timeline dock; per-dialogue/SFX sound lives in the Script Editor.
    const panelByTrack: Record<(typeof VALID_TIMELINE_TRACKS)[number], StudioPanelId> = {
      bgm: "timeline",
      sfx: "script_editor",
      dialogue: "script_editor",
      storyboard: "storyboard",
      video: "final_video",
    };
    this.panelManager.open(panelByTrack[trackId]);
  }

  /**
   * The project title is rendered as a button in the top strip; the
   * button's onClick opens a window.prompt — not something the bird
   * can drive cleanly. So we fly to the title (visual signal),
   * mutate the store directly, and the live-bound text updates.
   */
  private async choreographSetProjectTitle(title: string): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) {
      this.store.setProjectTitle(title);
      return;
    }
    try {
      const titleEl = this.queryAction<HTMLButtonElement>("project-title");
      if (titleEl) {
        await agent.flyTo(titleEl);
      }
      this.store.setProjectTitle(title);
      this.fireApplied(`~ project: ${title}`);
    } catch (error) {
      console.warn("[studio] set_project_title choreography failed, mutating directly:", error);
      this.store.setProjectTitle(title);
    } finally {
      agent.perch();
    }
  }

  // ------------------------------------------------------------------
  // Choreography helpers
  // ------------------------------------------------------------------

  /**
   * Open a panel and wait for the crossfade + first paint so freshly
   * mounted DOM is queryable.
   *
   * Pointing precision (hard requirement): when a switch is needed the
   * bird VISIBLY flies to and clicks the real sidebar nav entry
   * (`.studio-sidebar-item[data-tool="<panelId>"]`) — e.g. "change
   * locations" → fly to + click the Location entry, THEN act — instead of
   * silently calling `panelManager.open`. We fall back to a direct open
   * only when the nav button can't be resolved (e.g. the script lives in
   * its own column, not the sidebar) so the choreography still proceeds.
   */
  private async openPanelAndSettle(panelId: StudioPanelId): Promise<void> {
    const needsSwitch = this.panelManager.getCurrentPanelId() !== panelId;
    if (needsSwitch) {
      const navButton = document.querySelector<HTMLButtonElement>(
        `.studio-sidebar-item[data-tool="${cssEscape(panelId)}"]`,
      );
      if (this.actionAgent && navButton) {
        await this.actionAgent.flyTo(navButton);
        await this.actionAgent.click(navButton);
      } else {
        this.panelManager.open(panelId);
      }
    }
    if (this.actionAgent) {
      await this.actionAgent.waitForPaint();
      // Precision: don't fly to a control that hasn't painted yet. Poll
      // until the target surface actually contains actionable DOM, so the
      // bird points at the real element instead of missing it and
      // silently falling back to a direct mutation. The script editor
      // lives in its own persistent column, not the swappable host.
      const rootSelector =
        panelId === "script_editor"
          ? ".studio-script-dock"
          : ".studio-panel-host";
      await this.waitForRootPaint(rootSelector);
    }
  }

  /**
   * Resolve once `rootSelector` has painted at least one tagged
   * `[data-koko-action]` control (or the frame budget runs out). Keeps
   * fly-and-point landing on present elements after a panel switch /
   * re-render.
   */
  private async waitForRootPaint(
    rootSelector: string,
    maxFrames = 48,
  ): Promise<void> {
    for (let frame = 0; frame < maxFrames; frame += 1) {
      const host = document.querySelector(rootSelector);
      if (host && host.querySelector("[data-koko-action]")) return;
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve()),
      );
    }
  }

  /**
   * Find the studio element tagged with `data-koko-action="$key"`.
   * Returns null when missing — callers throw and fall back.
   */
  private queryAction<T extends HTMLElement>(key: string): T | null {
    return document.querySelector<T>(`[data-koko-action="${cssEscape(key)}"]`);
  }

  /**
   * Same as `queryAction` but scoped to a particular id (the
   * `data-id` attribute on the element). Used for per-card
   * controls (Edit / Save / Delete on a specific character or
   * asset card).
   */
  private queryActionForId<T extends HTMLElement>(
    key: string,
    id: string,
  ): T | null {
    return document.querySelector<T>(
      `[data-koko-action="${cssEscape(key)}"][data-id="${cssEscape(id)}"]`,
    );
  }

  private warnInvalid(block: ToolUseBlock, reason: string): void {
    console.warn(`[studio] tool ${block.name} ignored — ${reason}`, block.input);
  }

  /**
   * Record that a choreography step completed. The message is
   * collected into `actionsCollectedThisTurn` and ultimately
   * shipped to `onTurnComplete` after the queue drains, where the
   * host turns it into a "Koko reports back" follow-up Claude
   * call. We no longer flash pills here.
   */
  private fireApplied(message: string): void {
    console.info(`[studio] ${message}`);
    this.actionsCollectedThisTurn.push(message);
  }
}

/** A short, human-readable label for the live action queue overlay. */
function describeTool(block: ToolUseBlock): string {
  const input = (block.input ?? {}) as Record<string, unknown>;
  const name = typeof input.name === "string" ? input.name.trim() : "";
  const suffix = name ? ` · ${name}` : "";
  switch (block.name) {
    case "add_character": return `Add character${suffix}`;
    case "update_character": return `Update character${suffix}`;
    case "delete_character": return "Delete character";
    case "add_asset": return `Add asset${suffix}`;
    case "update_asset": return `Update asset${suffix}`;
    case "delete_asset": return "Delete asset";
    case "add_location": return `Add location${suffix}`;
    case "update_location": return `Update location${suffix}`;
    case "delete_location": return "Delete location";
    case "add_scene": return "Add scene";
    case "update_scene": return "Update scene";
    case "delete_scene": return "Delete scene";
    case "set_costume": return "Set costume";
    case "cast_scene": return "Cast scene";
    case "set_storyboard_status": return "Set storyboard";
    case "update_sound_design": return "Update sound";
    case "set_scene_camera": return "Set camera";
    case "jump_timeline_track": return "Jump timeline";
    case "set_project_title": return "Rename project";
    default: return block.name.replace(/_/g, " ");
  }
}

function readString(
  input: Record<string, unknown>,
  key: string,
): string | null {
  const value = input[key];
  if (typeof value === "string") return value;
  // Permissively coerce numbers/booleans — Claude occasionally swaps types.
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
}

function readBoolean(
  input: Record<string, unknown>,
  key: string,
): boolean | null {
  const value = input[key];
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return null;
}

function readNumber(
  input: Record<string, unknown>,
  key: string,
): number | null {
  const value = input[key];
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function readStringArray(
  input: Record<string, unknown>,
  key: string,
): string[] | null {
  const value = input[key];
  if (!Array.isArray(value)) return null;
  const out: string[] = [];
  for (const entry of value) {
    if (typeof entry === "string") out.push(entry);
    else if (typeof entry === "number") out.push(String(entry));
  }
  return out;
}

/**
 * Minimal CSS.escape polyfill for the `[data-koko-action="..."]`
 * attribute selectors. Studio IDs are simple `kind_baseN` strings
 * but we sanitize anyway in case the LLM hands back something with
 * unusual characters. Falls through to native CSS.escape when
 * available.
 */
function cssEscape(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
